import {
  Eye,
  CheckCircle2,
  X,
  Calendar,
  Circle
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { cn } from "../../lib/utils";

const IMAGE_BASE_URL = "https://beigexmemehouse.s3.amazonaws.com/beige/";

const cardHoverClass =
  "transition-all duration-300 ease-in-out " +
  "hover:border-[#e8d0aa] dark:hover:border-primary/50 " +
  "hover:shadow-[5px_5px_0px_0px_#e8d0aa] dark:hover:shadow-[5px_5px_0px_0px_rgba(232,208,170,0.2)] " +
  "hover:-translate-y-1 cursor-pointer";

const StatusBadge = ({ status }) => {
  const statusMap = {
    0: { label: "Pending", color: "text-amber-500", border: "border-amber-100", fill: "fill-amber-500" },
    1: { label: "Approved", color: "text-green-500", border: "border-green-100", fill: "fill-green-500" },
    2: { label: "Rejected", color: "text-red-500", border: "border-red-100", fill: "fill-red-500" },
  };

  const current = statusMap[status] || statusMap[0];

  return (
    <span className={cn("inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-medium border bg-white dark:bg-background shadow-sm", current.color, current.border)}>
      <Circle className={cn("w-2 h-2", current.fill)} />
      {current.label}
    </span>
  );
};

const AdminEquipmentRequestCard = ({ item, onViewDetails, onApprove, onReject }) => {
  const { theme } = useTheme();

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
  };

  const getDisplayDate = () => {
    if (item?.project?.event_date) {
      return formatDate(item.project.event_date);
    }
    const start = formatDate(item?.checkout_date);
    const end = formatDate(item?.expected_return_date);

    if (start && end) return `${start} - ${end}`;
    if (start) return start;
    return "No Date Provided";
  };

  const crewName = item?.crew_member?.full_name || "John Doe";
  const requestId = item?.equipment?.model_number || "N/A";
  const description = item?.equipment?.description || "No description available";
  const profilePhoto = item?.crew_member?.profile_photo
    ? `${IMAGE_BASE_URL}${item.crew_member.profile_photo}`
    : "/images/misc/tempProfile.jpg";

  const equipmentName = item?.equipment?.equipment_name || "Unknown Equipment";
  const category = item?.equipment?.category || "Equipment";
  const hourlyRate = item?.equipment?.hourly_rate || "0";

  const displayImage = item?.equipment?.photos?.[0]?.file_url
    ? `${IMAGE_BASE_URL}${item.equipment.photos[0].file_url}`
    : "/images/misc/rent.png";

  // Check if the request is still pending
  const isPending = item.admin_accept === 0 || item.admin_accept === null;

  return (
    <div
      className={cn(
        "group bg-[#FDFAF7] dark:bg-card px-6 py-8 rounded-[1.5rem] border border-[#E8DDD3] dark:border-border shadow-sm w-full",
        cardHoverClass
      )}
    >
      <div className="flex items-center justify-between mb-7">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={profilePhoto}
              alt={crewName}
              className="w-14 h-14 rounded-full object-cover border border-gray-100"
            />
            <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-foreground text-lg leading-tight">
              {crewName}
            </h3>
            <p className="text-xs text-slate-400 font-medium mt-1">
              {requestId}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-5 mb-7 px-1">
        <div className="flex items-center gap-2 text-slate-400 text-xs font-medium">
          <Calendar className="w-4 h-4" />
          <span>{getDisplayDate()}</span>
        </div>
      </div>

      <div className={cn("bg-gradient-to-b from-[#F2ECE4] to-white dark:from-muted/40 dark:to-card rounded-[1.25rem] border border-[#E8DDD3] dark:border-border p-1")}>
        <div className="relative h-64 w-full flex items-center justify-center p-8 overflow-hidden">
          <span className="absolute top-5 left-5 bg-white dark:bg-background px-4 py-1.5 rounded-lg text-[11px] font-bold shadow-sm border border-gray-100 z-10">
            {category}
          </span>
          <img
            src={displayImage}
            alt={equipmentName}
            className={cn(
                "max-h-full max-w-full object-contain mix-blend-multiply dark:mix-blend-normal scale-110",
                "transition-transform duration-500 ease-in-out group-hover:scale-[1.25]"
            )}
          />
        </div>

        <div className="px-5 pb-6">
          <div className="flex items-center justify-between gap-4 mb-1">
            <h4 className="font-bold text-gray-900 dark:text-foreground text-base truncate">
              {equipmentName}
            </h4>
            <StatusBadge status={item.admin_accept} />
          </div>
          <p className="text-sm text-gray-600 dark:text-muted-foreground mb-5 truncate">{description}</p>

          <div className="border-t border-[#EBDCCB] dark:border-border mb-5" />

          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm font-semibold">
              ${hourlyRate}/hour
            </span>

            <div className="flex gap-2">
              <button
                onClick={() => onViewDetails?.(item)}
                className="w-10 h-10 flex items-center justify-center bg-[#2D3139] rounded-lg text-white hover:opacity-90 shadow-sm"
              >
                <Eye className="w-4 h-4" />
              </button>

              {isPending && (
                <>
                  <button
                    onClick={() => onApprove?.(item)}
                    className="w-10 h-10 flex items-center justify-center bg-[#E8FBF2] rounded-lg text-green-600 border border-green-100 hover:bg-green-100"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onReject?.(item)}
                    className="w-10 h-10 flex items-center justify-center bg-[#FFF1F1] rounded-lg text-red-500 border border-red-100 hover:bg-red-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEquipmentRequestCard;
